import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Activity, Eye, EyeOff, ArrowRight, Zap } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const { user, login, register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      if (mode === "register") await register(email, password, name);
      else await login(email, password);
      toast.success("Welcome to Pipeline Doctor");
      navigate("/");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const handleDemoLogin = async () => {
    setError(""); setLoading(true);
    try {
      const demoEmail = process.env.REACT_APP_DEMO_EMAIL || "demo@pipelinedoctor.io";
      const demoPw = process.env.REACT_APP_DEMO_PASSWORD || "DemoAccess2024!";
      await login(demoEmail, demoPw);
      toast.success("Demo Mode Active"); navigate("/");
    }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-[#FAF9F6]" data-testid="login-page">
      {/* Left Panel */}
      <div className="w-full lg:w-[500px] flex flex-col justify-center px-10 sm:px-14 bg-white border-r border-[#E8E4DF]">
        <div className="max-w-[380px] w-full mx-auto">
          <div className="flex items-center gap-3.5 mb-14">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#1E3A5F]">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-[15px] font-extrabold tracking-tight text-[#2D3142]">Pipeline Doctor</h1>
              <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1E3A5F]/60">Data Reliability Platform</p>
            </div>
          </div>

          <h2 className="text-[28px] font-extrabold tracking-tight text-[#2D3142] mb-1.5">
            {mode === "login" ? "Welcome back" : "Get started"}
          </h2>
          <p className="text-[14px] text-[#6B7280] mb-8">
            {mode === "login" ? "Sign in to your control tower." : "Create your account to get started."}
          </p>

          {error && <div className="mb-5 p-3 rounded-lg text-sm bg-[#BE3455]/8 border border-[#BE3455]/20 text-[#BE3455]" data-testid="login-error">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[11px] font-semibold text-[#6B7280]">Full name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required data-testid="register-name-input" className="h-11 rounded-lg border-[#E8E4DF] bg-[#FAF9F6] text-[#2D3142] placeholder:text-[#9CA3AF] focus:border-[#1E3A5F]/40 focus:ring-[#1E3A5F]/15" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[11px] font-semibold text-[#6B7280]">Email address</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.io" required data-testid="login-email-input" className="h-11 rounded-lg border-[#E8E4DF] bg-[#FAF9F6] text-[#2D3142] placeholder:text-[#9CA3AF] focus:border-[#1E3A5F]/40 focus:ring-[#1E3A5F]/15" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[11px] font-semibold text-[#6B7280]">Password</Label>
              <div className="relative">
                <Input id="password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" required data-testid="login-password-input" className="h-11 rounded-lg border-[#E8E4DF] bg-[#FAF9F6] text-[#2D3142] placeholder:text-[#9CA3AF] pr-10 focus:border-[#1E3A5F]/40 focus:ring-[#1E3A5F]/15" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#2D3142] transition-colors" onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full h-11 rounded-lg font-semibold text-sm border-0 text-white shadow-md shadow-[#1E3A5F]/15 hover:shadow-[#1E3A5F]/25 transition-all bg-[#1E3A5F] hover:bg-[#162D4A]" disabled={loading} data-testid="login-submit-button">
              {loading ? "Signing in..." : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#E8E4DF]" /></div>
            <div className="relative flex justify-center"><span className="bg-white px-4 text-[10px] uppercase tracking-[0.15em] font-semibold text-[#9CA3AF]">or</span></div>
          </div>

          <Button variant="outline" className="w-full h-11 rounded-lg font-medium text-sm border-[#E8E4DF] bg-[#FAF9F6] hover:bg-[#F3F1EC] text-[#6B7280] hover:text-[#2D3142] transition-all" onClick={handleDemoLogin} disabled={loading} data-testid="demo-login-button">
            <Zap className="w-4 h-4 mr-2 text-[#E76F51]" />
            Launch Demo Mode
            <span className="ml-auto text-[9px] font-mono font-bold px-2 py-0.5 rounded bg-[#E76F51]/10 text-[#E76F51]">VIEWER</span>
          </Button>

          <p className="text-[13px] text-center mt-8 text-[#9CA3AF]">
            {mode === "login" ? (
              <>New here? <button onClick={() => { setMode("register"); setError(""); }} className="font-semibold text-[#1E3A5F] hover:underline" data-testid="switch-to-register">Create an account</button></>
            ) : (
              <>Already registered? <button onClick={() => { setMode("login"); setError(""); }} className="font-semibold text-[#1E3A5F] hover:underline" data-testid="switch-to-login">Sign in</button></>
            )}
          </p>
        </div>
      </div>

      {/* Right — Warm Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center relative overflow-hidden bg-gradient-to-br from-[#F5F3EE] to-[#EDE9E3]">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-[10%] left-[15%] w-48 h-48 rounded-full bg-[#1E3A5F]/[0.04]" />
          <div className="absolute bottom-[15%] right-[10%] w-72 h-72 rounded-full bg-[#2D6A4F]/[0.03]" />
          <div className="absolute top-[50%] left-[60%] w-32 h-32 rounded-full bg-[#E76F51]/[0.04]" />
        </div>
        <div className="text-center px-12 relative z-10 max-w-[440px]">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-8 flex items-center justify-center bg-[#1E3A5F]/[0.06] border border-[#1E3A5F]/10">
            <Activity className="w-8 h-8 text-[#1E3A5F]" />
          </div>
          <h3 className="text-[22px] font-extrabold text-[#2D3142] mb-3 tracking-tight">Data Reliability Control Tower</h3>
          <p className="text-[14px] leading-relaxed text-[#6B7280] mb-10">
            Monitor pipeline health, detect anomalies, trace data lineage, and orchestrate remediation workflows.
          </p>
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Pipelines", value: "20", color: "#1E3A5F" },
              { label: "Healthy", value: "14", color: "#2D6A4F" },
              { label: "Incidents", value: "6", color: "#BE3455" },
            ].map((s) => (
              <div key={s.label} className="p-4 rounded-xl bg-white border border-[#E8E4DF] shadow-sm">
                <div className="text-2xl font-extrabold" style={{ color: s.color }}>{s.value}</div>
                <div className="text-[9px] uppercase tracking-[0.2em] mt-1 font-semibold text-[#9CA3AF]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
